import { useAuth } from '@agentnet/auth';
import { createStyles, makeStyles, MenuItem } from '@material-ui/core';
import { getTeamUsers } from 'api/orderManagement/order-management-api';
import { ca } from 'date-fns/locale';
import {
  getMemberNames,
  RemittanceReceivablesCategory,
  RemittanceReceivablesColor,
  RemittanceReceivablesStatus,
  TeamAssignment,
} from 'features/orderManagement/enum';
import { RemittanceReceivable, UserDetailType } from 'features/orderManagement/types';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompleteSearch from 'ui-kit/inputs/AutocompleteSearch/AutocompleteSearch';
import { getFullName } from 'utilities/utilities';

interface FileEditAssignmentProps {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  data: RemittanceReceivable[];
  onSaved?: (oldTeamValue: TeamAssignment | null, newTeamValue: TeamAssignment | null) => void;
}

function firstOrDefault<T>(data: T[]): T | null {
  return data?.[0] || null;
}

function getRemittanceReceivableStatus(data: RemittanceReceivable[]): RemittanceReceivablesStatus | null {
  const first = firstOrDefault(data)?.status;
  return first && data?.every((datum) => datum.status === first)
    ? RemittanceReceivablesStatus[first as keyof typeof RemittanceReceivablesStatus]
    : null;
}

function getRemittanceReceivableCategory(data: RemittanceReceivable[]): string {
  const first = firstOrDefault(data)?.category;
  return first && data?.every((datum) => datum.category === first) ? first : '';
}

function getAssigneeName(data: RemittanceReceivable[]): string {
  const first = firstOrDefault(data)?.assignee;
  return first && data?.every((datum) => datum.assignee === first) ? first : '';
}

export const RemittanceReceivablesModifyAssignmentContainer = ({
  open,
  setOpen,
  data,
  onSaved,
}: FileEditAssignmentProps): JSX.Element => {
  const remittanceReceivableStatus = getRemittanceReceivableStatus(data);
  const remittanceReceivableCategory = getRemittanceReceivableCategory(data);
  const remittanceReceivableName = getAssigneeName(data);

  const [status, setStatus] = useState<RemittanceReceivablesStatus | null>(remittanceReceivableStatus);
  const [category, setCategory] = useState<string>(remittanceReceivableCategory);
  const [assigneeName, setAssigneeName] = useState<string>(remittanceReceivableName);
  const [fscUsers, setFscUsers] = useState<UserDetailType[]>([]);

  const { getAccessToken } = useAuth();

  const useStyles = makeStyles(() =>
    createStyles({
      routeContainer: {
        width: '100%',
      },
      circle: {
        width: 12,
        height: 12,
        display: 'inline-block',
        marginRight: 4,
        borderRadius: 12,
      },
      separator: {
        paddingBottom: 20,
      },
    }),
  );

  const classes = useStyles();

  const getFscUsers = async (): Promise<Array<UserDetailType>> => {
    const token = await getAccessToken();
    const ifscMembers = await getTeamUsers(Number(TeamAssignment.IFSC), token);
    const pfscMembers = await getTeamUsers(Number(TeamAssignment.PFSC), token);
    return [...ifscMembers, ...pfscMembers];
  };

  const {
    execute: executeGetFscUsers,
    status: getFscUsersStatus,
    value: getFscUsersValue,
  } = useAsync<Array<UserDetailType>>(getFscUsers, false);

  const getStatusSelectOptions = (): SelectOption[] => {
    const selectOptions: SelectOption[] = [];
    const statuses = getMemberNames(RemittanceReceivablesStatus);
    let status = statuses.next();
    while (!status.done) {
      selectOptions.push({
        value: RemittanceReceivablesStatus[status.value as keyof typeof RemittanceReceivablesStatus],
        name: status.value,
      });
      status = statuses.next();
    }
    return selectOptions;
  };

  const getCategorySelectOptions = (): SelectOption[] => {
    const selectOptions: SelectOption[] = [];
    const categories = getMemberNames(RemittanceReceivablesCategory);
    let category = categories.next();
    while (!category.done) {
      const name = RemittanceReceivablesCategory[category.value as keyof typeof RemittanceReceivablesCategory];
      selectOptions.push({
        value: name,
        name: name,
      });
      category = categories.next();
    }
    selectOptions.push({
      value: '',
      name: '',
    });
    return selectOptions;
  };

  const getAssigneeSelectOptions = (): SelectOption[] => {
    const assigneeSelectOptions: SelectOption[] = [];
    for (const fscUser of fscUsers) {
      const fullName = getFullName(fscUser);
      assigneeSelectOptions.push({
        //value: fscUser.userId,
        value: fullName,
        name: fullName,
        tooltip: fscUser.username,
      });
    }
    return assigneeSelectOptions;
  };

  const getStatusCustomMenuItems = (): JSX.Element[] => {
    const items: JSX.Element[] = [];
    const statuses = getMemberNames(RemittanceReceivablesStatus);
    let status = statuses.next();
    while (!status.done) {
      const statusKey = status.value;
      const statusValue = RemittanceReceivablesStatus[statusKey as keyof typeof RemittanceReceivablesStatus];
      const statusColor = RemittanceReceivablesColor[statusKey as keyof typeof RemittanceReceivablesColor];
      const menuItem = (
        <MenuItem key={statusKey} value={statusValue}>
          <span
            className={classes.circle}
            style={{ backgroundColor: '#' + statusColor.toString(16).padStart(6, '0') }}
          ></span>
          {statusKey}
        </MenuItem>
      );
      items.push(menuItem);
      status = statuses.next();
    }
    return items;
  };

  const onConfirm = () => {
    data.forEach((datum) => {
      datum.status = status || '';
      datum.category = category;
      datum.assignee = assigneeName || '';
    });
    setOpen(false);
    setStatus(null);
    setCategory('');
    setAssigneeName('');
  };

  const onDismissAction = () => {
    setStatus(null);
    setCategory('');
    setOpen(false);
    setAssigneeName('');
  };

  useEffect(() => {
    executeGetFscUsers();
  }, []);

  useEffect(() => {
    if (getFscUsersStatus === 'success' && getFscUsersValue) {
      setFscUsers(getFscUsersValue);
    }
  }, [getFscUsersStatus, getFscUsersValue]);

  return (
    <AgentNetConfirmationDialog
      open={open}
      dialogTitle="Modify Assignment"
      dialogTextHTML={
        <>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Status"
              name="Status"
              value={status}
              options={getStatusSelectOptions()}
              required
              customMenuItems={getStatusCustomMenuItems()}
              menuOption={(value: any) => {
                setStatus(value);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AgentNetDropdownSelector
              label="Category"
              name="Category"
              value={category}
              options={getCategorySelectOptions()}
              menuOption={(value: any) => {
                setCategory(value);
              }}
            />
          </div>
          <div className={classes.separator}>
            <AutocompleteSearch
              label="Assignee"
              options={getAssigneeSelectOptions()}
              hideSearchIcon
              onChange={(value) => {
                if (value && typeof value === 'string') {
                  setAssigneeName(value);
                }
              }}
              noMatchesLabel="No matching assignee found"
              disabled={fscUsers.length === 0}
              value={assigneeName ?? ''}
              hideNoMatches
            />
          </div>
        </>
      }
      dialogBtnContent="Save"
      secondaryActionBtnContent="Cancel"
      onDismissAction={onDismissAction}
      onConfirm={onConfirm}
      disableBackDropClick
    />
  );
};
